import { useMemo } from 'react'
import { PaymentPlanKey } from 'api/clients/PaymentPlanClient'
import { useRootStore } from 'stores/RootStore'
import { useBrandProfile } from 'hooks/queries/workspaces/useBrandProfile'
import { useCurrentBrandPlan } from 'hooks/queries/brandPlans/useCurrentBrandPlan'

export interface ContentTypeCosts {
  stillImage: number
  carousel: number
  feedVideo: number
  shortFormVideo: number
  story: number
  blog: number
  email: number
}

export const useContentTypeCosts = (options?: {
  applyVisualStyle?: boolean
  forceGenV2?: boolean
}): ContentTypeCosts => {
  const profile = useBrandProfile({ cacheTime: 5 * 60 * 1000 })
  const { data: brandPlan } = useCurrentBrandPlan({ cacheTime: 5 * 60 * 1000 })

  const hasStylizedImage =
    !!profile?.data?.brand_kit?.content_preferences?.apply_visual_style || !!options?.applyVisualStyle
  const hasAnimatedImage = !!profile?.data?.brand_kit?.content_preferences?.video_sources?.includes('animated_image')
  const aiVideoWeight = (profile?.data?.brand_kit?.content_preferences?.video_source_animated_image_weight || 0) / 100
  const brandKitStockVideoWeight =
    (profile?.data?.brand_kit?.content_preferences?.video_source_brand_kit_stock_weight || 0) / 100

  const {
    creditUsageStore: { creditUsage },
    tierUsageStore: { currentSubscription },
  } = useRootStore()

  const isStrictBrandControl = profile?.data?.brand_kit?.content_preferences?.source_image_edit_level === 'none'
  const isGenV2 = options?.forceGenV2 || (!!brandPlan?.has_content_strategies && !isStrictBrandControl)

  const isOnPioneer = currentSubscription?.plan === PaymentPlanKey.VirtualMarketer
  const usageCosts = creditUsage?.usage_costs
  const contentGenerationCost = isOnPioneer ? 0 : usageCosts?.content_credits_cost || 0
  const visualStylesCost = usageCosts?.visual_styles_credits_cost || 0
  const animatedImagesCost = usageCosts?.video_credits_cost || 0
  const stillImageCost = usageCosts?.still_image_credits_cost || 0
  const carouselCost = usageCosts?.carousel_credits_cost || 0
  const scriptVideoCost = usageCosts?.script_video_credits_cost || 0
  const blogPostCost = usageCosts?.blog_post_credits_cost || 0
  const emailCost = usageCosts?.newsletter_credits_cost || 0

  return useMemo((): ContentTypeCosts => {
    if (isGenV2) {
      return {
        stillImage: stillImageCost,
        carousel: carouselCost,
        feedVideo: scriptVideoCost,
        shortFormVideo: scriptVideoCost,
        story: stillImageCost,
        blog: blogPostCost,
        email: emailCost,
      }
    }

    // V1 rates derived from brand kit settings
    const stillImageRate = hasStylizedImage ? visualStylesCost : contentGenerationCost
    const feedVideoRate = hasAnimatedImage ? animatedImagesCost : contentGenerationCost
    const shortFormVideoRate = hasAnimatedImage
      ? aiVideoWeight * animatedImagesCost +
        brandKitStockVideoWeight * contentGenerationCost +
        (hasStylizedImage ? aiVideoWeight * visualStylesCost : 0)
      : contentGenerationCost

    return {
      stillImage: stillImageRate,
      carousel: carouselCost,
      feedVideo: feedVideoRate,
      shortFormVideo: shortFormVideoRate,
      story: stillImageRate,
      blog: contentGenerationCost + (hasStylizedImage ? visualStylesCost : 0),
      email: contentGenerationCost + (hasStylizedImage ? 3 * visualStylesCost : 0),
    }
  }, [
    isGenV2,
    hasStylizedImage,
    hasAnimatedImage,
    aiVideoWeight,
    brandKitStockVideoWeight,
    contentGenerationCost,
    visualStylesCost,
    animatedImagesCost,
    stillImageCost,
    carouselCost,
    scriptVideoCost,
    blogPostCost,
    emailCost,
  ])
}
